import React, { useCallback, useEffect, useState } from "react";
import AppLayout from "@/components/layout/AppLayout";
import api, { API_BASE, formatApiErrorDetail } from "@/lib/api";
import { formatDate, daysBetween } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Plus, Search, MoreHorizontal, Upload, Download, RefreshCw, Trash2, X as XIcon, Activity as ActivityIcon } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import ClientForm from "@/components/client/ClientForm";
import ClientImportDialog from "@/components/client/ClientImportDialog";
import ClientActivityDialog from "@/components/client/ClientActivityDialog";
import { useAuth } from "@/context/AuthContext";

export default function Clients() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [amlStatuses, setAmlStatuses] = useState([]);
  const [q, setQ] = useState("");
  const [amlFilter, setAmlFilter] = useState("");
  const [tlBucket, setTlBucket] = useState(""); // "" | "overdue" | "soon"
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [activityClient, setActivityClient] = useState(null);
  const [selected, setSelected] = useState(new Set());

  const canDelete = user?.role === "administrator";

  const allSelected = items.length > 0 && items.every((c) => selected.has(c.id));
  const toggleAll = () => setSelected((prev) => {
    const n = new Set(prev);
    if (allSelected) items.forEach((c) => n.delete(c.id));
    else items.forEach((c) => n.add(c.id));
    return n;
  });
  const toggleOne = (id) => setSelected((prev) => {
    const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n;
  });
  const clear = () => setSelected(new Set());
  const bulkDelete = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} client(s)? Clients with linked compliance records will be skipped.`)) return;
    try {
      const { data } = await api.post("/clients/bulk-delete", { ids });
      const parts = [`Deleted ${data.deleted}`];
      if (data.blocked) parts.push(`Skipped ${data.blocked} (have compliances)`);
      toast.success(parts.join(" · "));
      clear(); load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Bulk delete failed");
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (amlFilter) params.aml_status = amlFilter;
      if (tlBucket) params.tl_expiry_bucket = tlBucket;
      const { data } = await api.get("/clients", { params });
      setItems(data.items);
      setTotal(data.total);
      setAmlStatuses(data.aml_statuses || []);
    } catch (e) {
      toast.error("Failed to load clients");
    } finally {
      setLoading(false);
    }
  }, [q, amlFilter, tlBucket]);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);

  const remove = async (c) => {
    if (!window.confirm(`Delete client ${c.name}?`)) return;
    try {
      await api.delete(`/clients/${c.id}`);
      toast.success("Client deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <AppLayout
      title="Client Master"
      actions={
        <>
          <Button variant="outline" onClick={load} data-testid="refresh-btn"><RefreshCw className="w-4 h-4 mr-1.5" /> Refresh</Button>
          <Button variant="outline" onClick={() => window.open(`${API_BASE}/clients/export.csv`, "_blank")} data-testid="clients-export-csv"><Download className="w-4 h-4 mr-1.5" /> Export CSV</Button>
          <Button variant="outline" onClick={() => setShowImport(true)} data-testid="import-clients-btn"><Upload className="w-4 h-4 mr-1.5" /> Import CSV</Button>
          <Button onClick={() => { setEditing(null); setShowForm(true); }} data-testid="new-client-btn"><Plus className="w-4 h-4 mr-1.5" /> New Client</Button>
        </>
      }
    >
      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative max-w-md flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Search client name…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="client-search" />
          </div>
          <Select value={amlFilter || "all"} onValueChange={(v) => setAmlFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-[180px]" data-testid="client-filter-aml"><SelectValue placeholder="AML Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All AML statuses</SelectItem>
              {amlStatuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={tlBucket || "all"} onValueChange={(v) => setTlBucket(v === "all" ? "" : v)}>
            <SelectTrigger className="w-[190px]" data-testid="client-filter-tl-expiry"><SelectValue placeholder="TL Expiry" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All TL Expiry</SelectItem>
              <SelectItem value="overdue">Overdue</SelectItem>
              <SelectItem value="soon">Expiring ≤ 30 days</SelectItem>
            </SelectContent>
          </Select>
          {(amlFilter || tlBucket || q) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setQ(""); setAmlFilter(""); setTlBucket(""); }}
              data-testid="client-filter-reset"
            >
              Reset
            </Button>
          )}
        </div>
      </div>

      {selected.size > 0 && (
        <div className="mb-3 flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg px-4 py-2.5" data-testid="clients-bulk-bar">
          <div className="text-sm text-blue-900"><span className="font-semibold font-mono-num">{selected.size}</span> client(s) selected</div>
          <div className="flex items-center gap-2">
            {canDelete && (
              <Button variant="destructive" size="sm" onClick={bulkDelete} data-testid="clients-bulk-delete">
                <Trash2 className="w-4 h-4 mr-1.5" /> Delete selected
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={clear} data-testid="clients-bulk-clear">
              <XIcon className="w-4 h-4 mr-1.5" /> Clear
            </Button>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <Table>
          <TableHeader className="bg-slate-50">
            <TableRow>
              <TableHead className="px-3 w-10">
                <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="clients-select-all" />
              </TableHead>
              <TableHead className="px-4">Name</TableHead>
              <TableHead>Country</TableHead>
              <TableHead>VAT TRN</TableHead>
              <TableHead>Concerned Person</TableHead>
              <TableHead>Trade Licence</TableHead>
              <TableHead>Jurisdiction</TableHead>
              <TableHead>TL Expiry</TableHead>
              <TableHead>AML Status</TableHead>
              <TableHead>Email</TableHead>
              <TableHead className="text-right pr-4">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={11} className="text-center py-10 text-slate-500">Loading…</TableCell></TableRow>
            ) : items.length === 0 ? (
              <TableRow><TableCell colSpan={11} className="text-center py-16 text-slate-500">No clients yet. Click "New Client" or import a CSV.</TableCell></TableRow>
            ) : items.map((c) => (
              <TableRow key={c.id} className="data-row" data-testid={`client-row-${c.id}`}>
                <TableCell className="px-3">
                  <Checkbox checked={selected.has(c.id)} onCheckedChange={() => toggleOne(c.id)} data-testid={`client-check-${c.id}`} />
                </TableCell>
                <TableCell className="px-4 font-medium text-slate-900">{c.name}</TableCell>
                <TableCell className="text-slate-700 text-sm">{c.country || "—"}</TableCell>
                <TableCell className="text-slate-700 text-sm font-mono-num">{c.vat_trn || "—"}</TableCell>
                <TableCell className="text-slate-700 text-sm">{c.concerned_person_name || "—"}</TableCell>
                <TableCell className="text-slate-700 text-sm font-mono-num">{c.trade_license_number || "—"}</TableCell>
                <TableCell className="text-slate-700 text-sm">{c.licensing_authority || "—"}</TableCell>
                <TableCell className="text-sm font-mono-num">
                  {(() => {
                    if (!c.trade_license_expiry) return "—";
                    const d = daysBetween(c.trade_license_expiry);
                    const expired = d !== null && d < 0;
                    const soon = d !== null && d >= 0 && d <= 30;
                    let tone = "bg-white text-slate-700 border-transparent";
                    if (expired) tone = "bg-red-50 text-red-700 border-red-200 font-semibold";
                    else if (soon) tone = "bg-orange-50 text-orange-700 border-orange-200";
                    return (
                      <span
                        className={"inline-flex items-center px-2 py-0.5 rounded-md border " + tone}
                        data-testid={`tl-expiry-${c.id}`}
                      >
                        {formatDate(c.trade_license_expiry)}{expired ? ` · ${Math.abs(d)}d overdue` : ""}
                      </span>
                    );
                  })()}
                </TableCell>
                <TableCell className="text-slate-700 text-sm">{c.aml_status || "—"}</TableCell>
                <TableCell className="text-slate-700 text-sm">{c.email || "—"}</TableCell>
                <TableCell className="text-right pr-4">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" data-testid={`client-actions-${c.id}`}>
                        <MoreHorizontal className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => setActivityClient(c)} data-testid={`client-activity-${c.id}`}>
                        <ActivityIcon className="w-4 h-4 mr-2" /> View activity
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => { setEditing(c); setShowForm(true); }} data-testid={`client-edit-${c.id}`}>Edit</DropdownMenuItem>
                      {canDelete && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem className="text-red-600 focus:text-red-700" onClick={() => remove(c)} data-testid={`client-delete-${c.id}`}>Delete</DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <div className="px-4 py-3 border-t border-slate-200 text-sm text-slate-600">
          Showing <span className="font-medium">{items.length}</span> of <span className="font-medium font-mono-num">{total}</span> clients
        </div>
      </div>

      <ClientForm open={showForm} onClose={() => { setShowForm(false); setEditing(null); }} initial={editing} onSaved={load} />
      <ClientImportDialog open={showImport} onClose={() => setShowImport(false)} onDone={load} />
      <ClientActivityDialog open={!!activityClient} client={activityClient} onClose={() => setActivityClient(null)} />
    </AppLayout>
  );
}
